// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import * as TimesheetFieldsActions from "../../timesheet-fields/actions"
import { showSnackBar } from "../../snackbar/actions"
import { executor, Init, timesheetFieldService } from "../../../app/rpc"
import {
  ListTimesheetFieldsRequestSchema,
  ListTimesheetFieldsResponse,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type ListTimesheetFieldsRpc = (request: Init<typeof ListTimesheetFieldsRequestSchema>) => Observable<ListTimesheetFieldsResponse>

export const listTimesheetFields = (listTimesheetFieldsRpc: ListTimesheetFieldsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listTimesheetFields.match),
      mergeMap(action => listTimesheetFieldsRpc(action.payload.request)
        .pipe(
          map(t => t.timesheetFields),
          mergeMap(result => {
            const actions: Action[] = [
              Slice.setTimesheetFields({
                timesheetFields: result,
                siteId: action.payload.request.siteId ?? "",
              }),
            ]
            if (action.payload.includeOptions && result.length) {
              result.forEach(timesheetField => {
                actions.push(TimesheetFieldsActions.listTimesheetFieldOptions({
                  timesheetFieldId: timesheetField.id,
                  siteId: timesheetField.siteId,
                }))
              })
            }
            return concat(actions)
          }),
          catchError(() => of(showSnackBar({
            message: t({
              id: "msg_list_timesheet_fields_failed",
              message: "Failed to load timesheet fields. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
            }),
            messageType: "ERROR",
          }))),
        )
      ),
    )

const listTimesheetFieldsRpc = (request: Init<typeof ListTimesheetFieldsRequestSchema>): Observable<ListTimesheetFieldsResponse> =>
  executor(() => timesheetFieldService.listTimesheetFields(create(ListTimesheetFieldsRequestSchema, request)))

export default listTimesheetFields(listTimesheetFieldsRpc)
